import { useCallback, useMemo, useRef, useState } from 'react';
import { TaskCompletionFilterInputObject } from 'graphql-common';
import { useTaskCompletionsIdsLazyQuery } from '@lib/graphql-common';
import { Option } from '@lib/interfaces/form';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import useTaskCompletionDetailsActions from '@lib/components/TaskCompletions/utils/useTaskCompletionDetailsActions';
import useTaskCompletionQueryHook from '@lib/utils/fetch/hooks/useTaskCompletionQueryHook';
import { COMPARE_PAGE_SIZE } from '@lib/components/TaskCompletions/TaskCompletionsModal/CompletionDetails/utils/constants';

const NO_FILTERS: TaskCompletionFilterInputObject[] = [];

type Args = {
  groupKey: string | null | undefined;
  completionId?: string | null;
  onChangeCompletionId: (id: string) => void;
  isViewLocked?: boolean;
  orderingField?: string | null;
  orderingDirection?: string | null;
  /**
   * When set, the component is anchored on a fixed, user-picked set of
   * completions (bulk "Compare completions" action) instead of the whole
   * group — compare mode is then forced on and single-item navigation
   * doesn't apply. Expressed as an `id in [...]` filter on the ids query.
   */
  bulkCompareIds?: string[] | null;
  /**
   * Resolves the set of completions prev/next (and compare mode) browse.
   * When omitted, the whole group is browsed via `taskCompletionsIds` keyed
   * on `groupKey`. A host that shows a *filtered* list passes its own
   * `fetchIds` so navigation stays inside the set the user is looking at.
   */
  fetchIdsOverride?: () => Promise<FetchIdsResponse<unknown>>;
  // Narrows the default (non-override) sibling ids, e.g. to a timeline
  // bucket's period. Keep it referentially stable.
  baseFilters?: TaskCompletionFilterInputObject[];
};

export default function useTaskCompletionDetailsViewActions({
  groupKey,
  completionId,
  onChangeCompletionId,
  isViewLocked,
  orderingField,
  orderingDirection,
  bulkCompareIds,
  fetchIdsOverride,
  baseFilters = NO_FILTERS,
}: Args) {
  const isBulkCompare = !!bulkCompareIds;
  const [statusFilter, setStatusFilter] = useState<Option[]>([]);
  const [isCompareMode, setIsCompareMode] = useState(isBulkCompare);
  const [compareAnchorIndex, setCompareAnchorIndex] = useState(0);
  const [hasFetchedIdsOnce, setHasFetchedIdsOnce] = useState(false);
  // Only ever written once a sibling-ids fetch has *settled* (see below) so
  // it never flips mid-fetch — otherwise every status-filter
  // change would bounce the view between the empty state and the (stale)
  // completion table while the new ids are in flight.
  const [isEmpty, setIsEmpty] = useState(false);

  // Reset synchronously during render (not via useEffect) whenever a
  // different group is opened. This component isn't remounted when the
  // group changes (only groupKey/completionId props change), so a
  // `hasFetchedIdsOnce`/statusFilter/`isEmpty` left over from the *previous*
  // group would otherwise leak into the new group's first paint for one
  // frame: if the previous group had resolved to zero ids, `isEmpty` would
  // read true immediately, hiding the new completion behind the generic
  // empty state instead of the loading spinner. A useEffect reset runs one
  // tick too late to prevent that frame.
  const groupKeyRef = useRef(groupKey);
  const isGroupChange = groupKeyRef.current !== groupKey;
  if (isGroupChange) {
    groupKeyRef.current = groupKey;
    if (statusFilter.length) setStatusFilter([]);
    if (hasFetchedIdsOnce) setHasFetchedIdsOnce(false);
    if (isEmpty) setIsEmpty(false);
    if (!isBulkCompare && isCompareMode) setIsCompareMode(false);
    if (compareAnchorIndex !== 0) setCompareAnchorIndex(0);
  }

  const [fetchIdsQuery] = useTaskCompletionsIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  // The status filter is a question-level concern: each answer carries its
  // own `validityHint`, and the filter narrows which question *rows* are
  // shown in the currently viewed completion(s) — entirely client-side (see
  // TaskCompletionDetails/TaskCompletionsCompareTable) — same
  // meaning whether you arrive via a single "Completion details" row or via
  // bulk "Compare completions". It never narrows which *completions* this
  // hook resolves/browses, so it's never part of the ids-query filters, and
  // changing it doesn't trigger a `taskCompletionsIds` refetch. Bulk
  // compare's `id in [...]` clause fixes the compared set to the user's
  // selection; otherwise only the host's `baseFilters` scope apply.
  const filters = useMemo<TaskCompletionFilterInputObject[]>(
    () =>
      isBulkCompare
        ? [{ id: { predicate: { in: bulkCompareIds || [] } } }]
        : baseFilters,
    [isBulkCompare, bulkCompareIds, baseFilters],
  );

  const fetchGroupIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          groupKey: groupKey || '',
          filters: filters.length ? filters : undefined,
          sorts:
            orderingField && orderingDirection
              ? { [orderingField]: orderingDirection }
              : undefined,
        },
      }),
    [fetchIdsQuery, groupKey, filters, orderingField, orderingDirection],
  );

  // Bulk compare pins the set to the user's selection, so it always resolves
  // through the `id in [...]` query above even when a host override exists.
  const fetchIds = isBulkCompare
    ? fetchGroupIds
    : fetchIdsOverride || fetchGroupIds;

  const {
    allIds,
    idsLoading,
    onNavigatePrev,
    onNavigateNext,
    canNavigatePrev,
    canNavigateNext,
  } = useTaskCompletionDetailsActions({
    completionId,
    onChangeCompletionId,
    fetchIds,
  });

  // `idsLoading` only flips true once the fetch effect has run (a tick after
  // mount), so gating emptiness on it directly would flash the empty state
  // for one frame on every open — only trust it once a fetch has actually
  // started, then take `isEmpty` from each settled result. Synced during
  // render, but never in the same pass as the group reset above, which
  // writes the same state.
  if (!isGroupChange) {
    if (idsLoading && !hasFetchedIdsOnce) {
      setHasFetchedIdsOnce(true);
    } else if (
      hasFetchedIdsOnce &&
      !idsLoading &&
      isEmpty !== (allIds.length === 0)
    ) {
      setIsEmpty(allIds.length === 0);
    }
  }

  const currentIndex = completionId ? allIds.indexOf(completionId) : -1;

  const comparePageIds = useMemo(
    () =>
      allIds.slice(compareAnchorIndex, compareAnchorIndex + COMPARE_PAGE_SIZE),
    [allIds, compareAnchorIndex],
  );
  const canCompareNavigatePrev = compareAnchorIndex > 0;
  const canCompareNavigateNext =
    compareAnchorIndex + COMPARE_PAGE_SIZE < allIds.length;

  const onCompareNavigatePrev = useCallback(() => {
    setCompareAnchorIndex((prev) => Math.max(0, prev - COMPARE_PAGE_SIZE));
  }, []);

  const onCompareNavigateNext = useCallback(() => {
    setCompareAnchorIndex((prev) =>
      prev + COMPARE_PAGE_SIZE < allIds.length
        ? prev + COMPARE_PAGE_SIZE
        : prev,
    );
  }, [allIds.length]);

  const onToggleCompareMode = useCallback(
    (value: boolean) => {
      if (isBulkCompare) return;
      setIsCompareMode(value);
      if (value) {
        const maxAnchorIndex = Math.max(0, allIds.length - COMPARE_PAGE_SIZE);
        setCompareAnchorIndex(
          currentIndex >= 0 ? Math.min(currentIndex, maxAnchorIndex) : 0,
        );
      } else {
        const anchorId = allIds[compareAnchorIndex];
        if (anchorId) onChangeCompletionId(anchorId);
      }
    },
    [
      isBulkCompare,
      currentIndex,
      allIds,
      compareAnchorIndex,
      onChangeCompletionId,
    ],
  );

  const { data, loading, firstLoading, error } = useTaskCompletionQueryHook({
    id: completionId || '',
    isViewLocked,
    skip: isEmpty || isCompareMode,
  });

  return {
    statusFilter,
    setStatusFilter,
    allIds,
    allIdsLoading: idsLoading,
    currentIndex,
    isEmpty,
    onNavigatePrev,
    onNavigateNext,
    canNavigatePrev,
    canNavigateNext,
    completionData: data,
    completionLoading: loading,
    completionFirstLoading: firstLoading,
    completionError: error,
    isBulkCompare,
    isCompareMode,
    onToggleCompareMode,
    comparePageIds,
    compareTotalCount: allIds.length,
    compareAnchorIndex,
    onCompareNavigatePrev,
    onCompareNavigateNext,
    canCompareNavigatePrev,
    canCompareNavigateNext,
  };
}
